import React from 'react';

import { AppNavigation } from '@lib/types';
import { Badge } from '@components/Badge';
import { useNavigation } from '@hooks';
import { Button, ButtonProps } from '@components/Button';
import { Icon, IconName } from '@components/Icon';
import { makeStyles, useTheme } from '@providers/Theme';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: 2,
    justifyContent: 'flex-start',
    borderWidth: 0,
    elevation: 0,
    boxShadow: '',
    ...theme.mixins.padding(1),
  },
  text: {
    color: theme.palette.cool[700],
    flex: 6,
  },
  active: {
    backgroundColor: theme.palette.primary[50],
    color: theme.palette.primary[600],
  },
}));

export type SidebarButtonProps = {
  label: React.ReactNode;
  activeRouteNames?: Partial<Record<keyof AppNavigation, true>>;
  onPress?: () => void;
  startIcon?: IconName;
  endIcon?: IconName;
  badgeLabel?: number;
  TypographyProps?: ButtonProps['TypographyProps'];
  testID?: string;
  accessibilityLabel?: string;
  className?: string;
};

export const SidebarButton: React.FC<SidebarButtonProps> = ({
  label,
  activeRouteNames = {},
  onPress,
  startIcon,
  endIcon,
  TypographyProps = {},
  testID,
  badgeLabel,
  accessibilityLabel,
  className,
}) => {
  const styles = useStyles();
  const navigation = useNavigation();
  const theme = useTheme();

  const isActive = activeRouteNames[navigation.currentRoute];

  return (
    <Button
      testID={testID}
      className={className}
      accessibilityLabel={accessibilityLabel}
      fullWidth
      variant="outlined"
      style={[styles.root, isActive && styles.active]}
      onPress={() => onPress?.()}
      TypographyProps={{
        style: [styles.text, isActive && styles.active],
        ...TypographyProps,
      }}
      startIcon={
        startIcon ? (
          <Icon name={startIcon} color={theme.palette.cool[700]} />
        ) : undefined
      }
      endIcon={
        endIcon ? (
          <Icon name={endIcon} color={theme.palette.cool[700]} />
        ) : undefined
      }
      badge={
        badgeLabel ? <Badge label={badgeLabel} preset="error" /> : undefined
      }>
      {label}
    </Button>
  );
};
